/**
 * Smart layout engine for mindmap node placement.
 *
 * Rule: place the new child as close to the parent as possible without
 * overlapping any existing node. Implemented as a 1-D interval-skyline
 * search on the target column:
 *
 *  1. Pick the target column X (parent.x + HORIZONTAL_OFFSET, or the
 *     rightmost existing sibling column).
 *  2. Build blocked Y-intervals from every visible node whose bbox
 *     overlaps that column (cousins, manually-repositioned nodes, etc.),
 *     padded by SIBLING_GAP so a new node placed in any free gap is
 *     automatically separated from neighbours.
 *  3. Pick the Y closest to parent.y that fits a new node inside one of
 *     the free gaps.
 *  4. If the closest free slot is more than MAX_COLUMN_HEIGHT from the
 *     parent (and siblings already exist in the column), wrap the new
 *     child to a fresh column SIBLING_COLUMN_OFFSET to the right.
 *
 * Drop-in replacement for calculateChildPosition in nodePositioning.ts.
 */

import type { Node, Edge } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { SIBLING_GAP, DEFAULT_NODE_HEIGHT, type Position } from './nodePositioning'
import { NODE_DEFAULTS } from '../../../utils/nodeDefaults'

// ============================================================================
// CONSTANTS
// ============================================================================

const HORIZONTAL_OFFSET = 600
const MIN_Y = 50
const MAX_COLUMN_HEIGHT = 2000
const SIBLING_COLUMN_OFFSET = 650

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

type Interval = readonly [number, number]

function nodeBottom(n: Node<ReactFlowNodeData>): number {
  return n.position.y + (n.measured?.height ?? DEFAULT_NODE_HEIGHT)
}

function nodeRight(n: Node<ReactFlowNodeData>): number {
  return n.position.x + (n.measured?.width ?? NODE_DEFAULTS.width)
}

/**
 * True if a node placed at `targetColumnX` with standard width would
 * visually overlap node `n` on the X axis. Catches both same-column
 * siblings and adjacent nodes whose bbox crosses the column boundary.
 */
function overlapsTargetColumn(n: Node<ReactFlowNodeData>, targetColumnX: number): boolean {
  if (n.hidden) return false
  const newNodeRight = targetColumnX + (NODE_DEFAULTS.width as number)
  return n.position.x < newNodeRight && nodeRight(n) > targetColumnX
}

function mergeIntervals(intervals: Interval[]): Interval[] {
  if (intervals.length === 0) return []
  const sorted = [...intervals].sort((a, b) => a[0] - b[0])
  const merged: Array<[number, number]> = [[sorted[0][0], sorted[0][1]]]
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]
    const [s, e] = sorted[i]
    if (s <= last[1]) {
      last[1] = Math.max(last[1], e)
    } else {
      merged.push([s, e])
    }
  }
  return merged
}

/**
 * Find the Y closest to `idealY` such that a new node of height
 * `newNodeHeight` placed at that Y does not intersect any blocked
 * interval. Blocked intervals must already include desired gap padding.
 */
function findClosestFreeY(
  blockedIntervals: Interval[],
  idealY: number,
  newNodeHeight: number
): number {
  const merged = mergeIntervals(blockedIntervals)

  type Gap = { start: number; end: number }
  const gaps: Gap[] = []
  let prevEnd = -Infinity
  for (const [s, e] of merged) {
    if (s > prevEnd) gaps.push({ start: prevEnd, end: s })
    prevEnd = Math.max(prevEnd, e)
  }
  gaps.push({ start: prevEnd, end: Infinity })

  let best: number = idealY
  let bestDist = Infinity
  for (const gap of gaps) {
    const minY = Math.max(gap.start, MIN_Y)
    const maxY = gap.end - newNodeHeight
    if (maxY < minY) continue
    const candidate = Math.max(minY, Math.min(idealY, maxY))
    const dist = Math.abs(candidate - idealY)
    if (dist < bestDist) {
      bestDist = dist
      best = candidate
    }
  }
  return best
}

function buildBlockedIntervals(
  nodes: Node<ReactFlowNodeData>[],
  targetColumnX: number
): Interval[] {
  return nodes
    .filter((n) => overlapsTargetColumn(n, targetColumnX))
    .map((n) => [n.position.y - SIBLING_GAP, nodeBottom(n) + SIBLING_GAP] as Interval)
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Calculates the position for a new child node of `parentNode`.
 */
export function smartPlaceChild(
  parentNode: Node<ReactFlowNodeData>,
  nodes: Node<ReactFlowNodeData>[],
  edges: Edge[]
): Position {
  const siblings = edges
    .filter((e) => e.source === parentNode.id)
    .map((e) => nodes.find((n) => n.id === e.target))
    .filter((n): n is Node<ReactFlowNodeData> => n !== undefined && !n.hidden)

  const targetColumnX =
    siblings.length === 0
      ? parentNode.position.x + HORIZONTAL_OFFSET
      : Math.max(...siblings.map((s) => s.position.x))

  const idealY = Math.max(MIN_Y, parentNode.position.y)

  const blocked = buildBlockedIntervals(nodes, targetColumnX)
  const candidateY = findClosestFreeY(blocked, idealY, DEFAULT_NODE_HEIGHT)

  // Wrap to a fresh column only when the closest free slot is too far from
  // the parent — and only if siblings already anchor a column to wrap from.
  if (siblings.length > 0 && Math.abs(candidateY - idealY) > MAX_COLUMN_HEIGHT) {
    const wrappedX = targetColumnX + SIBLING_COLUMN_OFFSET
    const wrappedIdealY = Math.max(MIN_Y, Math.min(...siblings.map((s) => s.position.y)))
    const wrappedBlocked = buildBlockedIntervals(nodes, wrappedX)
    const wrappedY = findClosestFreeY(wrappedBlocked, wrappedIdealY, DEFAULT_NODE_HEIGHT)
    return { x: wrappedX, y: Math.max(MIN_Y, wrappedY) }
  }

  return { x: targetColumnX, y: Math.max(MIN_Y, candidateY) }
}
